<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Calculator, CircleDollarSign, Plus, RefreshCw, Search, Trash2 } from 'lucide-vue-next'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import { formatDisplayDate } from '@yuksalish/ui'
import PaginationControls from '../../components/PaginationControls.vue'
import ProductPickerModal from '../components/ProductPickerModal.vue'
import ReferenceItemPickerModal from '../components/ReferenceItemPickerModal.vue'
import {
  calculateCosting,
  getProduct,
  listCostCalculations,
  listProductBomVersions,
  getBomVersion,
} from '../productService'
import { isManufacturedProduct } from '../productPresentation'
import type {
  CostCalculationSnapshot,
  CostingComponentInput,
  CostingComponentType,
  CostingResult,
  CostingSummary,
  EngineeringProduct,
  ExpenseReference,
  OperationReference,
} from '../types'

const route = useRoute()
const client = useApiClient()
const auth = useAuthStore()
const { t } = useI18n()

const product = ref<EngineeringProduct | null>(null)
const loading = ref(false)
const pickerOpen = ref(false)
const materialPickerOpen = ref(false)
const operationPickerOpen = ref(false)
const expensePickerOpen = ref(false)
const errorMessage = ref('')
const formError = ref('')
const calculating = ref(false)
const result = ref<CostingResult | null>(null)
const history = ref<CostCalculationSnapshot[]>([])
const historyTotal = ref(0)
const historyTotalPages = ref(1)
const historyLoading = ref(false)
const historyError = ref('')
const historyPage = ref(1)
const historyPageSize = ref(5)
const productionType = ref('socks')
const currency = ref('UZS')
const components = ref<CostingComponentInput[]>([])
const pickerTarget = ref<CostingComponentInput | null>(null)

const canCalculate = computed(
  () => auth.user?.permissions.includes('production_orders.manage') ?? false,
)
const canViewHistory = computed(
  () => auth.user?.permissions.includes('products.view') ?? false,
)
const hasProduct = computed(() => product.value != null)
const productId = computed(() => Number(route.query.product_id))
const hasValidProductId = computed(
  () => Number.isInteger(productId.value) && productId.value > 0,
)

const groupedComponents = computed(() => {
  const groups: Record<CostingComponentType, CostingComponentInput[]> = {
    material: [],
    labor: [],
    overhead: [],
    waste: [],
    profit: [],
  }
  components.value.forEach((component) => {
    groups[component.type].push(component)
  })
  return groups
})

const materialTotal = computed(() =>
  groupedComponents.value.material.reduce((sum, item) => sum + (item.amount ?? 0), 0),
)
const laborTotal = computed(() =>
  groupedComponents.value.labor.reduce((sum, item) => sum + (item.amount ?? 0), 0),
)
const overheadTotal = computed(() =>
  groupedComponents.value.overhead.reduce((sum, item) => sum + (item.amount ?? 0), 0),
)

function createComponent(type: CostingComponentType): CostingComponentInput {
  const base: CostingComponentInput = {
    name: '',
    type,
    group: type === 'labor' ? 'labor' : type === 'overhead' ? 'overhead' : type === 'material' ? 'knitting' : undefined,
    calculation_mode: 'fixed',
    amount: 0,
  }
  if (type === 'waste') {
    return {
      name: t('engineering.costing.wasteDefault'),
      type,
      calculation_mode: 'percent_of_group',
      target_group: 'knitting',
      percent: 4.5,
    }
  }
  if (type === 'profit') {
    return {
      name: t('engineering.costing.profitDefault'),
      type,
      calculation_mode: 'percent_of_total',
      percent: 15,
    }
  }
  return base
}

function addComponent(type: CostingComponentType): void {
  components.value.push(createComponent(type))
}

function removeComponent(index: number): void {
  components.value.splice(index, 1)
}

function openMaterialPicker(target?: CostingComponentInput): void {
  pickerTarget.value = target ?? null
  materialPickerOpen.value = true
}

function openOperationPicker(target?: CostingComponentInput): void {
  pickerTarget.value = target ?? null
  operationPickerOpen.value = true
}

function openExpensePicker(target?: CostingComponentInput): void {
  pickerTarget.value = target ?? null
  expensePickerOpen.value = true
}

function applyOrCreateComponent(
  type: CostingComponentType,
  patch: Partial<CostingComponentInput>,
): void {
  const target = pickerTarget.value
  if (target) {
    Object.assign(target, patch)
    return
  }
  const component = createComponent(type)
  Object.assign(component, patch)
  components.value.push(component)
}

function selectMaterialComponent(selected: EngineeringProduct): void {
  materialPickerOpen.value = false
  applyOrCreateComponent('material', {
    name: selected.name,
    group: 'knitting',
    quantity_per_unit: selected.min_order_quantity ?? undefined,
    source_unit_cost: 0,
    uom: selected.unit || undefined,
  })
  pickerTarget.value = null
}

function selectOperationComponent(selected: OperationReference | ExpenseReference): void {
  if (selected.type !== 'operation') return
  operationPickerOpen.value = false
  applyOrCreateComponent('labor', {
    name: selected.name,
    group: 'labor',
    amount: selected.base_amount ?? 0,
  })
  pickerTarget.value = null
}

function selectExpenseComponent(selected: OperationReference | ExpenseReference): void {
  if (selected.type !== 'expense_type') return
  expensePickerOpen.value = false
  applyOrCreateComponent('overhead', {
    name: selected.name,
    group: 'overhead',
    amount: selected.base_amount ?? 0,
  })
  pickerTarget.value = null
}

function pickerLabel(component: CostingComponentInput): string {
  if (component.type === 'material') {
    return t('engineering.costing.selectMaterial')
  }
  if (component.type === 'labor') {
    return t('engineering.costing.selectOperation')
  }
  return t('engineering.costing.selectExpense')
}

function componentHasPicker(component: CostingComponentInput): boolean {
  return component.type === 'material'
    || component.type === 'labor'
    || component.type === 'overhead'
}

function componentNameReadonly(component: CostingComponentInput): boolean {
  return component.type === 'material' || component.type === 'labor'
}

function openPickerFor(component: CostingComponentInput): void {
  if (component.type === 'material') {
    openMaterialPicker(component)
  } else if (component.type === 'labor') {
    openOperationPicker(component)
  } else if (component.type === 'overhead') {
    openExpensePicker(component)
  }
}

async function loadProduct(): Promise<void> {
  if (!hasValidProductId.value) return
  loading.value = true
  errorMessage.value = ''
  try {
    const loaded = await getProduct(client, productId.value)
    if (!isManufacturedProduct(loaded)) {
      product.value = null
      errorMessage.value = t('engineering.costing.invalidProduct')
      return
    }
    product.value = loaded
    await prefillFromBom()
  } catch (error: unknown) {
    product.value = null
    errorMessage.value = formatApiError(error)
  } finally {
    loading.value = false
  }
}

async function prefillFromBom(): Promise<void> {
  if (!product.value) return
  try {
    const versions = await listProductBomVersions(client, product.value.id)
    const active = versions.find((version) => Boolean(version.is_active)) ?? versions[0]
    if (!active) return
    const bom = await getBomVersion(client, active.id)
    const seed: CostingComponentInput[] = []

    bom.operations.forEach((operation) => {
      operation.materials.forEach((material) => {
        seed.push({
          name: material.material_name || `Material #${material.material_id}`,
          type: 'material',
          group: 'knitting',
          calculation_mode: 'fixed',
          amount: 0,
          quantity_per_unit: material.quantity,
          source_unit_cost: 0,
          uom: material.material_unit || undefined,
        })
      })
      if (operation.cost_per_produced_unit > 0) {
        seed.push({
          name: operation.operation_name || `Operation #${operation.operation_sequence}`,
          type: 'labor',
          group: 'labor',
          calculation_mode: 'fixed',
          amount: operation.cost_per_produced_unit,
        })
      }
    })

    seed.push(createComponent('waste'))
    seed.push(createComponent('profit'))
    components.value = seed
  } catch {
    // BOM prefill is best-effort; the user can still build components manually.
  }
}

function selectProduct(selected: EngineeringProduct): void {
  product.value = selected
  pickerOpen.value = false
  components.value = []
  result.value = null
  history.value = []
  historyTotal.value = 0
  historyTotalPages.value = 1
  historyError.value = ''
  historyPage.value = 1
  void prefillFromBom()
  if (canViewHistory.value) void loadHistory()
}

function validate(): boolean {
  formError.value = ''
  if (!product.value) {
    formError.value = t('engineering.costing.productRequired')
    return false
  }
  if (!components.value.length) {
    formError.value = t('engineering.costing.componentsRequired')
    return false
  }
  const invalidIndex = components.value.findIndex((component) => {
    if (!component.name.trim()) return true
    if (component.calculation_mode === 'fixed') {
      return component.amount == null || component.amount < 0
    }
    if (component.calculation_mode === 'percent_of_group' && !component.target_group) return true
    return component.percent == null || component.percent < 0
  })
  if (invalidIndex >= 0) {
    formError.value = t('engineering.costing.componentInvalid', { index: invalidIndex + 1 })
    return false
  }
  return true
}

async function runCalculation(): Promise<void> {
  if (!canCalculate.value) return
  if (!validate()) return
  calculating.value = true
  formError.value = ''
  try {
    result.value = await calculateCosting(client, {
      product_id: product.value!.id,
      production_type: productionType.value.trim() || 'socks',
      currency: currency.value.trim() || 'UZS',
      components: components.value.map((component) => ({
        ...component,
        amount: component.calculation_mode === 'fixed' ? Number(component.amount ?? 0) : undefined,
        percent: component.calculation_mode !== 'fixed' ? Number(component.percent ?? 0) : undefined,
      })),
    })
    await loadHistory()
  } catch (error: unknown) {
    result.value = null
    formError.value = formatApiError(error)
  } finally {
    calculating.value = false
  }
}

async function loadHistory(): Promise<void> {
  if (!product.value) return
  if (!canViewHistory.value) return
  historyLoading.value = true
  historyError.value = ''
  try {
    const page = await listCostCalculations(client, product.value.id, {
      production_type: productionType.value.trim() || 'socks',
      page: historyPage.value,
      per_page: historyPageSize.value,
    })
    history.value = page.items
    historyTotal.value = page.total
    historyTotalPages.value = page.total_pages
    if (historyPage.value > page.total_pages) {
      historyPage.value = Math.max(1, page.total_pages)
    }
  } catch (error: unknown) {
    history.value = []
    historyTotal.value = 0
    historyTotalPages.value = 1
    historyError.value = formatApiError(error)
  } finally {
    historyLoading.value = false
  }
}

function snapshotComponents(snapshot: CostCalculationSnapshot): CostingComponentInput[] {
  const raw = snapshot.input_snapshot
  if (Array.isArray(raw)) {
    return raw.map((component) => ({ ...component }))
  }
  if (raw && Array.isArray((raw as { components?: unknown }).components)) {
    return (raw as { components: CostingComponentInput[] }).components.map(
      (component) => ({ ...component }),
    )
  }
  return []
}

function snapshotFinalCost(snapshot: CostCalculationSnapshot): number | null {
  const output = snapshot.output_snapshot
  if (output && typeof output === 'object' && 'summary' in output) {
    const summary = (output as { summary?: CostingSummary }).summary
    if (summary && typeof summary.final_cost === 'number') {
      return summary.final_cost
    }
  }
  return null
}

function loadSnapshotIntoEditor(snapshot: CostCalculationSnapshot): void {
  const loaded = snapshotComponents(snapshot)
  if (!loaded.length) {
    formError.value = t('engineering.costing.snapshotEmpty')
    return
  }
  components.value = loaded
  productionType.value = snapshot.production_type || productionType.value
  const raw = snapshot.input_snapshot
  if (!Array.isArray(raw) && raw && typeof (raw as { currency?: string }).currency === 'string') {
    currency.value = (raw as { currency: string }).currency
  }
  result.value = null
  formError.value = ''
  document.getElementById('components-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function formatMoney(value: number | undefined | null): string {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
  }).format(Number(value ?? 0))
}

onMounted(loadProduct)
watch([historyPage, historyPageSize], () => {
  if (product.value) void loadHistory()
})
</script>

<template>
  <div class="page-toolbar">
    <RouterLink class="back-link" :data-tooltip="$t('engineering.action.backProducts')" to="/products">{{ $t('engineering.action.backProducts') }}</RouterLink>
    <div class="page-toolbar__actions">
      <button type="button" :disabled="!canCalculate" :title="$t('engineering.costing.permissionHint')" @click="runCalculation">
        <Calculator :size="16" aria-hidden="true" />
        {{ calculating ? $t('engineering.costing.calculating') : $t('engineering.costing.calculate') }}
      </button>
    </div>
  </div>

  <div v-if="loading" class="detail-state">{{ $t('engineering.state.loadingProduct') }}</div>
  <div v-else-if="errorMessage" class="detail-state detail-state--error">
    <p role="alert">{{ errorMessage }}</p>
    <button v-if="hasValidProductId" type="button" @click="loadProduct">{{ $t('engineering.action.retry') }}</button>
  </div>

  <template v-else>
    <header class="page-heading">
      <div>
        <p class="eyebrow">{{ $t('engineering.costing.eyebrow') }}</p>
        <h2>{{ $t('engineering.costing.title') }}</h2>
        <p>{{ $t('engineering.costing.description') }}</p>
      </div>
    </header>

    <section class="product-section">
      <div class="section-heading">
        <div>
          <h3>{{ $t('engineering.costing.product') }}</h3>
          <p>{{ $t('engineering.costing.productHelp') }}</p>
        </div>
        <button type="button" class="secondary-button" @click="pickerOpen = true">
          <RefreshCw :size="16" aria-hidden="true" />
          {{ $t('engineering.costing.changeProduct') }}
        </button>
      </div>
      <div v-if="product" class="product-summary">
        <div class="product-media">
          <span v-if="product.name">{{ product.name.slice(0, 1).toUpperCase() }}</span>
        </div>
        <div>
          <strong>{{ product.name }}</strong>
          <span>{{ product.sku }} · {{ product.type }} · #{{ product.id }}</span>
        </div>
      </div>
      <div v-else class="empty-product">
        <p>{{ $t('engineering.costing.noProduct') }}</p>
        <button type="button" @click="pickerOpen = true">{{ $t('engineering.costing.selectProduct') }}</button>
      </div>
    </section>

    <section v-if="product" id="components-section" class="components-section">
      <div class="section-heading">
        <div>
          <h3>{{ $t('engineering.costing.components') }}</h3>
          <p>{{ $t('engineering.costing.componentsHelp') }}</p>
        </div>
      </div>
      <div class="button-group-toolbar" role="toolbar" :aria-label="t('engineering.costing.componentsToolbar')">
        <button type="button" class="button-group-toolbar__item" @click="openMaterialPicker()"><Plus :size="16" aria-hidden="true" />{{ $t('engineering.costing.addMaterial') }}</button>
        <button type="button" class="button-group-toolbar__item" @click="openOperationPicker()"><Plus :size="16" aria-hidden="true" />{{ $t('engineering.costing.addLabor') }}</button>
        <button type="button" class="button-group-toolbar__item" @click="openExpensePicker()"><Plus :size="16" aria-hidden="true" />{{ $t('engineering.costing.addOverhead') }}</button>
        <button type="button" class="button-group-toolbar__item" @click="addComponent('waste')"><Plus :size="16" aria-hidden="true" />{{ $t('engineering.costing.addWaste') }}</button>
        <button type="button" class="button-group-toolbar__item" @click="addComponent('profit')"><Plus :size="16" aria-hidden="true" />{{ $t('engineering.costing.addProfit') }}</button>
      </div>

      <div v-if="!components.length" class="section-state">{{ $t('engineering.costing.noComponents') }}</div>

      <template v-else>
        <div class="component-table-wrap">
          <table class="component-table">
            <thead>
              <tr>
                <th class="col-name">{{ $t('engineering.costing.name') }}</th>
                <th class="col-mode">{{ $t('engineering.costing.mode') }}</th>
                <th class="col-value">{{ $t('engineering.costing.amount') }}</th>
                <th class="col-uom">{{ $t('engineering.costing.uom') }}</th>
                <th class="col-target">{{ $t('engineering.costing.targetGroup') }}</th>
                <th class="col-action" :aria-label="t('engineering.costing.remove')"></th>
              </tr>
            </thead>
            <tbody
              v-for="(group, type) in groupedComponents"
              :key="type"
              v-show="group.length"
              class="component-table-body"
            >
              <tr class="group-band">
                <td colspan="6">{{ $t(`engineering.costing.type.${type}`) }}</td>
              </tr>
              <tr class="component-table-row" v-for="(component, index) in group" :key="`${type}-${index}`">
                <td class="col-name">
                  <span class="name-field">
                    <input
                      v-model="component.name"
                      :readonly="componentNameReadonly(component)"
                      :class="{ 'name-field__readonly': componentNameReadonly(component) }"
                      :placeholder="t('engineering.costing.namePlaceholder')"
                    >
                    <button
                      v-if="componentHasPicker(component)"
                      type="button"
                      class="browse-button"
                      :aria-label="pickerLabel(component)"
                      :title="pickerLabel(component)"
                      @click="openPickerFor(component)"
                    ><Search :size="14" aria-hidden="true" /></button>
                  </span>
                </td>
                <td class="col-mode">
                  <select v-model="component.calculation_mode">
                    <option value="fixed">{{ $t('engineering.costing.modeFixed') }}</option>
                    <option value="percent_of_total">{{ $t('engineering.costing.modePercentTotal') }}</option>
                    <option value="percent_of_group">{{ $t('engineering.costing.modePercentGroup') }}</option>
                  </select>
                </td>
                <td class="col-value">
                  <input
                    v-if="component.calculation_mode === 'fixed'"
                    v-model.number="component.amount"
                    type="number"
                    min="0"
                    step="any"
                  >
                  <input
                    v-else
                    v-model.number="component.percent"
                    type="number"
                    min="0"
                    step="any"
                  >
                </td>
                <td class="col-uom">
                  <input
                    v-if="component.calculation_mode === 'fixed'"
                    v-model="component.uom"
                    :placeholder="t('engineering.costing.uomPlaceholder')"
                    aria-label="UOM"
                  >
                  <span v-else class="cell-static">%</span>
                </td>
                <td class="col-target">
                  <input
                    v-if="component.calculation_mode === 'percent_of_group'"
                    v-model="component.target_group"
                    :placeholder="t('engineering.costing.targetGroupPlaceholder')"
                  >
                  <span v-else class="cell-static"></span>
                </td>
                <td class="col-action">
                  <button type="button" class="remove-button" :aria-label="t('engineering.costing.remove')" @click="removeComponent(components.indexOf(component))"><Trash2 :size="16" aria-hidden="true" /></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="totals">
          <div><span>{{ $t('engineering.costing.materialTotal') }}</span><strong>{{ formatMoney(materialTotal) }}</strong></div>
          <div><span>{{ $t('engineering.costing.laborTotal') }}</span><strong>{{ formatMoney(laborTotal) }}</strong></div>
          <div><span>{{ $t('engineering.costing.overheadTotal') }}</span><strong>{{ formatMoney(overheadTotal) }}</strong></div>
        </div>

        <div class="options">
          <label>{{ $t('engineering.costing.productionType') }}<input v-model="productionType" :placeholder="t('engineering.costing.productionTypePlaceholder')"></label>
          <label>{{ $t('engineering.costing.currency') }}<input v-model="currency" :placeholder="t('engineering.costing.currencyPlaceholder')"></label>
        </div>

        <p v-if="formError" class="error-message" role="alert">{{ formError }}</p>
      </template>
    </section>

    <section v-if="result" class="result-section">
      <div class="section-heading">
        <div>
          <h3>{{ $t('engineering.costing.result') }}</h3>
          <p>{{ $t('engineering.costing.resultHelp') }}</p>
        </div>
        <span class="result-id">{{ $t('engineering.costing.calculation', { id: result.calculation_id }) }}</span>
      </div>
      <div class="summary-grid">
        <div><span>{{ $t('engineering.costing.baseCost') }}</span><strong>{{ formatMoney(result.summary.base_cost) }}</strong></div>
        <div><span>{{ $t('engineering.costing.wasteCost') }}</span><strong>{{ formatMoney(result.summary.waste_cost) }}</strong></div>
        <div><span>{{ $t('engineering.costing.costBeforeProfit') }}</span><strong>{{ formatMoney(result.summary.cost_before_profit) }}</strong></div>
        <div><span>{{ $t('engineering.costing.profitCost') }}</span><strong>{{ formatMoney(result.summary.profit_cost) }}</strong></div>
        <div class="summary-final"><span>{{ $t('engineering.costing.finalCost') }}</span><strong>{{ formatMoney(result.summary.final_cost) }} {{ result.currency }}</strong></div>
      </div>
      <div class="result-table">
        <table>
          <thead>
            <tr>
              <th>{{ $t('engineering.costing.name') }}</th>
              <th>{{ $t('engineering.costing.type') }}</th>
              <th>{{ $t('engineering.costing.group') }}</th>
              <th>{{ $t('engineering.costing.mode') }}</th>
              <th>{{ $t('engineering.costing.amount') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(component, index) in result.components" :key="index">
              <td><strong>{{ component.name }}</strong></td>
              <td>{{ $t(`engineering.costing.type.${component.type}`) }}</td>
              <td>{{ component.group || '—' }}</td>
              <td>{{ $t(`engineering.costing.modeLabel.${component.calculation_mode}`) }}</td>
              <td>{{ formatMoney(component.amount) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-if="canViewHistory" class="history-section">
      <div class="section-heading">
        <div>
          <h3>{{ $t('engineering.costing.history') }}</h3>
          <p>{{ $t('engineering.costing.historyHelp') }}</p>
        </div>
        <button v-if="product" type="button" class="secondary-button" @click="loadHistory"><RefreshCw :size="16" aria-hidden="true" />{{ $t('engineering.costing.refresh') }}</button>
      </div>
      <div v-if="!product" class="section-state">{{ $t('engineering.costing.historyNoProduct') }}</div>
      <div v-else-if="historyLoading" class="section-state">{{ $t('engineering.costing.historyLoading') }}</div>
      <div v-else-if="historyError" class="section-error" role="alert">
        <span>{{ historyError }}</span>
        <button type="button" @click="loadHistory">{{ $t('engineering.action.retry') }}</button>
      </div>
      <div v-else-if="!history.length" class="section-state">{{ $t('engineering.costing.historyEmpty') }}</div>
      <div v-else class="history-table">
        <table>
          <thead>
            <tr>
              <th>{{ $t('engineering.costing.calculation') }}</th>
              <th>{{ $t('engineering.costing.finalCost') }}</th>
              <th>{{ $t('engineering.costing.createdBy') }}</th>
              <th>{{ $t('engineering.costing.createdAt') }}</th>
              <th class="history-actions-head">{{ $t('engineering.costing.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="snapshot in history" :key="snapshot.id">
              <td><strong>#{{ snapshot.id }}</strong></td>
              <td>
                <template v-if="snapshotFinalCost(snapshot) != null">
                  {{ formatMoney(snapshotFinalCost(snapshot)!) }}
                </template>
                <template v-else>—</template>
              </td>
              <td>{{ snapshot.created_by_user_name || '—' }}</td>
              <td>{{ formatDisplayDate(snapshot.created_at) || '—' }}</td>
              <td>
                <button
                  type="button"
                  class="load-button"
                  :title="t('engineering.costing.loadIntoEditor')"
                  :aria-label="t('engineering.costing.loadIntoEditor')"
                  @click="loadSnapshotIntoEditor(snapshot)"
                ><Search :size="14" aria-hidden="true" />{{ $t('engineering.costing.loadIntoEditor') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
        <PaginationControls
          v-model:page="historyPage"
          v-model:page-size="historyPageSize"
          :total="historyTotal"
        />
      </div>
    </section>
  </template>

  <ProductPickerModal
    :open="pickerOpen"
    :title="t('engineering.costing.selectProduct')"
    :product-type="'manufactured_good'"
    :product-types="['manufactured_good', 'finished_good', 'semi_finished_good', 'semi_finished']"
    @close="pickerOpen = false"
    @select="selectProduct"
  />
  <ProductPickerModal
    :open="materialPickerOpen"
    :title="t('engineering.costing.selectMaterial')"
    :product-type="'raw_material'"
    @close="materialPickerOpen = false"
    @select="selectMaterialComponent"
  />
  <ReferenceItemPickerModal
    :open="operationPickerOpen"
    :title="t('engineering.costing.selectOperation')"
    type="operation"
    :eyebrow="t('engineering.costing.picker.operationsEyebrow')"
    :search-label="t('engineering.costing.picker.searchOperations')"
    :search-placeholder="t('engineering.costing.picker.searchNameOrCode')"
    :empty-message="t('engineering.costing.picker.noOperations')"
    :create-title="t('engineering.costing.createOperation')"
    :create-base-amount-label="t('engineering.costing.picker.costPerUnit')"
    @close="operationPickerOpen = false"
    @select="selectOperationComponent"
  />
  <ReferenceItemPickerModal
    :open="expensePickerOpen"
    :title="t('engineering.costing.selectExpense')"
    type="expense_type"
    :eyebrow="t('engineering.costing.picker.expensesEyebrow')"
    :search-label="t('engineering.costing.picker.searchExpenses')"
    :search-placeholder="t('engineering.costing.picker.searchNameOrCode')"
    :empty-message="t('engineering.costing.picker.noExpenses')"
    :create-title="t('engineering.costing.createExpense')"
    :create-base-amount-label="t('engineering.costing.picker.costPerUnit')"
    @close="expensePickerOpen = false"
    @select="selectExpenseComponent"
  />
</template>

<style scoped>
.back-link { display: inline-block; margin-bottom: 20px; color: #285b9a; font-size: 13px; font-weight: 700; }
.page-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.page-toolbar__actions button { display: inline-flex; gap: 8px; align-items: center; padding: 10px 15px; border: 0; color: #fff; background: #3568a8; font-weight: 700; cursor: pointer; }
.page-toolbar__actions button:disabled { opacity: 0.5; cursor: default; }
.page-heading { display: flex; gap: 24px; align-items: flex-end; justify-content: space-between; margin-bottom: 24px; }
.eyebrow { margin: 0 0 5px; color: #66726e; font-size: 12px; font-weight: 700; text-transform: uppercase; }
h2 { margin: 0; font-size: 26px; letter-spacing: 0; }
.page-heading p:last-child { margin: 7px 0 0; color: #66726e; }
section { margin-bottom: 16px; padding: 22px; border: 1px solid #d7dfdc; background: #fff; }
.section-heading { display: flex; gap: 18px; align-items: flex-start; justify-content: space-between; }
.section-heading h3 { margin: 0 0 4px; font-size: 16px; }
.section-heading p { margin: 0; color: #66726e; font-size: 13px; }
.button-group-toolbar { display: inline-flex; align-items: stretch; overflow: hidden; border: 1px solid #aebdcc; border-radius: 8px; background: #fff; box-shadow: 0 1px 2px rgb(23 52 84 / 8%); }
.button-group-toolbar__item { display: inline-flex; gap: 6px; align-items: center; padding: 9px 13px; border: 0; border-left: 1px solid #e1e7e5; color: #285b9a; background: #fff; font: inherit; font-size: 13px; font-weight: 700; cursor: pointer; white-space: nowrap; }
.button-group-toolbar__item:first-child { border-left: 0; }
.button-group-toolbar__item:hover { background: #eef4fb; }
.button-group-toolbar__item:active { background: #3568a8; color: #fff; }
.button-group-toolbar__item:focus-visible { outline: 2px solid #3568a8; outline-offset: -2px; }
.button-group-toolbar__item:disabled { color: #9aa7a3; background: #f3f6f5; cursor: default; }
.secondary-button { display: inline-flex; gap: 6px; align-items: center; padding: 8px 12px; border: 1px solid #b9c5c1; color: #285b9a; background: #fff; font: inherit; font-weight: 700; cursor: pointer; }
.product-summary { display: flex; gap: 14px; align-items: center; margin-top: 14px; }
.product-media { display: grid; width: 52px; height: 52px; color: #fff; background: #3568a8; font-size: 24px; font-weight: 700; place-items: center; }
.product-summary strong { display: block; color: #173454; }
.product-summary span { color: #66726e; font-size: 13px; }
.empty-product { display: grid; gap: 12px; margin-top: 14px; padding: 18px; border: 1px dashed #b9c5c1; color: #66726e; }
.empty-product button { justify-self: start; padding: 9px 14px; border: 1px solid #3568a8; color: #285b9a; background: #fff; font: inherit; font-weight: 700; cursor: pointer; }
.component-table-wrap { margin-top: 14px; overflow-x: auto; border: 1px solid #d7dfdc; }
.component-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.component-table thead th {
  padding: 7px 10px;
  border: 1px solid #d7dfdc;
  border-bottom: 2px solid #b9c5c1;
  background: #f4f7f6;
  color: #53625d;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  text-align: left;
  white-space: nowrap;
}
.component-table .col-name { width: 34%; }
.component-table .col-mode { width: 18%; }
.component-table .col-value { width: 14%; }
.component-table .col-uom { width: 10%; }
.component-table .col-target { width: 16%; }
.component-table .col-action { width: 52px; }
.component-table tbody .group-band td {
  padding: 5px 10px;
  border: 1px solid #d7dfdc;
  background: #eef4fb;
  color: #285b9a;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.component-table tbody .component-table-row td {
  padding: 3px 5px;
  border: 1px solid #e1e7e5;
  vertical-align: middle;
}
.component-table tbody .component-table-row:nth-child(even) td { background: #fafcfb; }
.component-table input, .component-table select {
  width: 100%;
  padding: 5px 7px;
  border: 1px solid #c8d2cd;
  background: #fff;
  font: inherit;
  font-size: 12px;
  box-sizing: border-box;
}
.component-table .name-field { display: flex; gap: 4px; align-items: stretch; }
.component-table .name-field input { flex: 1 1 auto; min-width: 0; }
.component-table .name-field input.name-field__readonly { color: #53625d; background: #f4f7f6; cursor: default; }
.component-table .cell-static { display: block; padding: 5px 7px; color: #9aa7a3; text-align: center; }
.browse-button { display: grid; width: 28px; min-width: 28px; padding: 0; border: 1px solid #b9c5c1; color: #285b9a; background: #fff; cursor: pointer; place-items: center; }
.browse-button:hover { background: #eef4fb; }
.browse-button:active { background: #3568a8; color: #fff; }
.remove-button { display: grid; width: 28px; height: 28px; margin: 0 auto; padding: 0; border: 1px solid #e4b8b1; color: #852d22; background: #fff8f6; cursor: pointer; place-items: center; }
.remove-button:hover { background: #fde8e4; }
.remove-button svg { display: block; }
.totals { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-top: 14px; }
.totals div { display: grid; gap: 4px; padding: 12px; border: 1px solid #d7dfdc; background: #f7faf9; }
.totals span { color: #66726e; font-size: 12px; }
.totals strong { font-size: 17px; }
.options { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top: 14px; }
.options label { display: grid; gap: 5px; font-size: 12px; font-weight: 700; color: #53625d; }
.options input { padding: 8px; border: 1px solid #b9c5c1; font: inherit; }
.error-message { margin: 14px 0 0; color: #852d22; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; margin-top: 14px; }
.summary-grid div { display: grid; gap: 4px; padding: 14px; border: 1px solid #d7dfdc; background: #f7faf9; }
.summary-grid span { color: #66726e; font-size: 12px; }
.summary-grid strong { font-size: 18px; }
.summary-grid .summary-final { grid-column: 1 / -1; border-color: #3568a8; background: #eef4fb; }
.summary-grid .summary-final strong { color: #285b9a; font-size: 22px; }
.result-id { color: #53625d; font-size: 12px; white-space: nowrap; }
.result-table, .history-table { margin-top: 14px; overflow-x: auto; }
table { width: 100%; border-collapse: collapse; }
th, td { padding: 10px; border-bottom: 1px solid #e1e7e5; text-align: left; }
th { font-size: 12px; text-transform: uppercase; color: #66726e; }
td strong { color: #173454; }
.history-actions-head { text-align: right; }
.load-button { display: inline-flex; gap: 6px; align-items: center; padding: 7px 10px; border: 1px solid #b9c5c1; color: #285b9a; background: #fff; font: inherit; font-size: 12px; font-weight: 700; cursor: pointer; white-space: nowrap; }
.load-button:hover { background: #eef4fb; }
.load-button:active { background: #3568a8; color: #fff; }
.section-state { display: grid; min-height: 120px; margin-top: 14px; color: #66726e; place-items: center; }
.section-error { display: flex; gap: 12px; align-items: center; margin-top: 14px; color: #852d22; }
.section-error button { padding: 7px 12px; border: 1px solid #b9c5c1; color: #285b9a; background: #fff; font: inherit; font-weight: 700; cursor: pointer; }
.detail-state { display: grid; min-height: 260px; color: #66726e; place-items: center; }
.detail-state--error { color: #852d22; }
.detail-state button { padding: 8px 14px; border: 1px solid #b9c5c1; color: #285b9a; background: #fff; font: inherit; font-weight: 700; cursor: pointer; }

@media (max-width: 900px) {
  .component-table .col-name { width: 30%; }
  .component-table .col-mode { width: 20%; }
  .component-table .col-value { width: 16%; }
  .component-table .col-uom { width: 12%; }
  .component-table .col-target { width: 14%; }
  .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .totals { grid-template-columns: 1fr; }
  .options { grid-template-columns: 1fr; }
}

@media (max-width: 760px) {
  .section-heading { align-items: stretch; flex-direction: column; }
  .button-group-toolbar { flex-wrap: wrap; width: 100%; border-radius: 8px; }
  .button-group-toolbar__item { flex: 1 1 auto; border-left: 0; border-top: 1px solid #e1e7e5; justify-content: center; }
  .button-group-toolbar__item:first-child { border-top: 0; }
  .component-table .col-target { display: none; }
  .component-table .col-uom { width: 14%; }
}
</style>
